import type { FC } from 'react';
import { Tag } from '@carbon/react';
import { formatNumber } from 'utils/number';
import type { PreDrillRuleBreakdown } from 'types/api-responses';
import { SCORING_RULE_LABELS, pctScore } from './utils';
import styles from './target-zone-suggestion-tile.module.scss';

// Real metric only when a finite number; BE placeholders (mock strings, nulls)
// render as an em dash.
export const asNum = (value: unknown): number | null => {
  return typeof value === 'number' && Number.isFinite(value) ? value : null;
};

export interface TargetZoneTileField {
  label: string;
  value: string;
}

// Build the four metric rows both the AI and User Selected cards show. `asNum`
// normalizes raw values (mock strings or numbers) to a number or em-dash.
export const zoneMetricFields = (metrics: {
  topTvdFt: unknown;
  bottomTvdFt: unknown;
  thicknessFt: unknown;
  offsetsInZone: unknown;
  nearbyPermitsInZone: unknown;
}): TargetZoneTileField[] => {
  const top = asNum(metrics.topTvdFt);
  const bottom = asNum(metrics.bottomTvdFt);
  const thickness = asNum(metrics.thicknessFt);
  const offsets = asNum(metrics.offsetsInZone);
  const permits = asNum(metrics.nearbyPermitsInZone);
  return [
    {
      label: 'Depth Range',
      value:
        top !== null && bottom !== null
          ? `${formatNumber(top, 0, 0)}–${formatNumber(bottom, 0, 0)} ft TVD`
          : '—',
    },
    {
      label: 'Thickness',
      value: thickness !== null ? `${formatNumber(thickness, 0, 0)} ft` : '—',
    },
    {
      label: 'Offsets in Zone',
      value: offsets !== null ? `${formatNumber(offsets, 0, 0)} wells` : '—',
    },
    {
      label: 'Nearby Permits in Zone',
      value: permits !== null ? `${formatNumber(permits, 0, 0)} permits` : '—',
    },
  ];
};

// Only the two Carbon Tag colors these cards use — narrowed to what's needed
// rather than re-copying the full 12-member tag-color union.
type TagColor = 'cyan' | 'teal';

interface TargetZoneTileProps {
  // Header eyebrow, e.g. "AI Suggestion 1" or "User Selected".
  eyebrow: string;
  // Optional header tag (e.g. "Recommended" / "Manual"); omit for none.
  tag?: { label: string; type: TagColor };
  // Geologic interval name (already resolved to a display string).
  name: string;
  fields: TargetZoneTileField[];
  // Pre-formatted confidence display, e.g. "42%" or "N/A".
  confidence: string;
  breakdown: Record<string, PreDrillRuleBreakdown>;
  // Render the breakdown section even when there are no signals (the AI card
  // always shows it; the manual card only shows it when the pick was scored).
  alwaysShowBreakdown?: boolean;
}

// Shared presentational card for a target-zone tile — the AI suggestion and
// manual "User Selected" cards are the same layout over different data.
export const TargetZoneTile: FC<TargetZoneTileProps> = ({
  eyebrow,
  tag,
  name,
  fields,
  confidence,
  breakdown,
  alwaysShowBreakdown = false,
}) => {
  // Sort signals by weight (highest first); stable sort keeps ties in order.
  const rules = Object.entries(breakdown ?? {}).sort(
    ([, a], [, b]) => b.weight - a.weight
  );
  const showBreakdown = alwaysShowBreakdown || rules.length > 0;

  return (
    <div className={styles.tile}>
      <div className={styles.header}>
        <span className={styles.rank}>
          {eyebrow}
          {tag && (
            <Tag type={tag.type} size="sm">
              {tag.label}
            </Tag>
          )}
        </span>
      </div>

      <p className={styles.interval}>{name}</p>

      <dl className={styles.fields}>
        {fields.map((field) => (
          <div key={field.label} className={styles.field}>
            <dt>{field.label}</dt>
            <dd>{field.value}</dd>
          </div>
        ))}
      </dl>

      <div className={styles.scoreSection}>
        <hr className={styles.divider} />
        <div className={styles.confidence}>
          <span className={styles.confidenceLabel}>Confidence</span>
          <span className={styles.confidenceValue}>{confidence}</span>
        </div>

        {showBreakdown && (
          <>
            <hr className={styles.divider} />
            <p className={styles.breakdownLabel}>Confidence Breakdown</p>
            <table className={styles.scoreTable}>
              <thead>
                <tr>
                  <th>Factor</th>
                  <th>Weight</th>
                  <th>Score</th>
                </tr>
              </thead>
              <tbody>
                {rules.map(([key, value]) => (
                  <tr key={key}>
                    <td>{SCORING_RULE_LABELS[key] ?? key}</td>
                    <td>{pctScore(value.weight)}</td>
                    <td>{pctScore(value.score)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </>
        )}
      </div>
    </div>
  );
};
